import { makeStyles } from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField/TextField";
import { Clear } from "@material-ui/icons";
import Autocomplete, { AutocompleteRenderInputParams } from "@material-ui/lab/Autocomplete";
import Checkbox from "@material-ui/core/Checkbox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import React, { forwardRef, ReactElement, Ref, useEffect, useImperativeHandle, useState } from "react";
import { v4 as uuid } from "uuid";
import PagedResponse from "../../api/models/PagedResponse";
import OrderedRequestHelper from "../../helper/OrderedRequestHelper";
import useDebounce from "../../hooks/useDebounce.hook";
import { match, parse } from "../../lib/AutosuggestHighlight";
import { tesaDarkGrey, tesaGrey6, tesaRed, tesaWhite } from "../../styles/colors";
import { AutocompleteMultipleTagData } from "./AutocompleteMultipleTagData.model";
import { AutocompleteSelectable } from "./AutocompleteSelectable.model";

const useStyles = makeStyles({
  root: {
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
      alignItems: "baseline",
      paddingLeft: "13px",
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input": {
      paddingBottom: "8px",
      paddingLeft: 0,
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input::placeholder": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-tag:first-child": {
      marginTop: "23.825px",
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-tag": {
      marginLeft: 0,
      marginRight: "10px",
    },
    "& .MuiChip-root": {
      fontSize: "0.875rem",
      height: "1.25rem",
    },
    "& .MuiChip-label": {
      fontSize: "1em",
      lineHeight: 1,
    },
    "& .MuiChip-colorPrimary": {
      backgroundColor: tesaDarkGrey,
    },
    "& .MuiChip-colorSecondary": {
      backgroundColor: tesaGrey6,
    },
    "& .MuiChip-deleteIcon": {
      fontSize: "1em",
      lineHeight: 1,
      width: "1em",
      height: "1em",
      marginRight: "0.5rem",
    },
    "& .MuiChip-deleteIconColorPrimary": {
      color: tesaWhite,
    },
  },
  error: {
    color: `${tesaRed} !important`,
  },
  listbox: {
    padding: 0,
  },
  option: {
    paddingLeft: "12px",
  },
  optionCheckbox: {
    padding: 0,
    marginRight: "12px",
  },
  optionLabel: {
    alignItems: "center",
    display: "flex",
    whiteSpace: "pre",
  },
});

type OptionFetchPaged<T> = (term: string) => Promise<PagedResponse<T>>;

const checkBoxIcon = <CheckBoxOutlineBlankIcon />;
const checkBoxCheckedIcon = <CheckBoxIcon />;

function isReset(event: React.ChangeEvent<HTMLElement>) {
  return event?.currentTarget?.classList.contains("MuiAutocomplete-clearIndicator");
}

export interface CustomSearchAutocompleteMultipleProps<T> {
  customTag?: React.ElementType;
  disabled?: boolean;
  disabledTextEntry?: boolean;
  error?: boolean;
  freeSolo?: boolean;
  freeze?: boolean;
  excludedOptions?: T[];
  helperText?: string;
  inputLabel: string;
  keepTermOnChange?: boolean;
  mandatory?: boolean;
  optionFetch: OptionFetchPaged<T>;
  optionsCheckbox?: boolean;
  value?: T[];
  getCustomTagDelete?: (
    item: AutocompleteSelectable<T>,
    index: number,
  ) => { onDelete: (event?: any) => void } | undefined;
  map: (data: T, index: number) => { key: string; label: string };
  onChange: (event: React.ChangeEvent<unknown>, value: T[]) => void;
  onInputChange?: (event: React.ChangeEvent<unknown>, value: string) => void;
  onTagClick?: (tagData: AutocompleteMultipleTagData<T>) => void;
  placeholder?: string;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  stringToData?: (name: string) => T;
  tagColorSecondary?: (tagIndex: number) => boolean;
}

function CustomSearchAutocompleteMultiple<T>(
  props: CustomSearchAutocompleteMultipleProps<T>,
  ref: Ref<{ reset: () => void }> | undefined,
): ReactElement {
  const classes = useStyles(props);
  const {
    customTag,
    disabled,
    disabledTextEntry,
    error,
    freeSolo,
    excludedOptions,
    helperText,
    inputLabel,
    keepTermOnChange,
    mandatory,
    placeholder,
    map,
    renderInput,
    stringToData,
    getCustomTagDelete,
    tagColorSecondary,
    onChange,
    onTagClick,
    optionFetch,
    optionsCheckbox,
    value,
    ...TextFieldProps
  } = props;

  if (freeSolo && !stringToData) {
    throw new Error("CustomSearchAutocompleteMultiple: must provide stringToData when set to freeSolo");
  }

  const mapDataValue = (data: T, index: number): AutocompleteSelectable<T> => {
    const { key, label } = map(data, index);
    return {
      key,
      label,
      data,
    };
  };

  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [freeze, setFreeze] = useState(false);
  const [orderedRequestHelper] = useState(new OrderedRequestHelper());

  const debouncedTerm = useDebounce(term, 300);
  const [options, setOptions] = useState<AutocompleteSelectable<T>[]>([]);
  const excludedValues = excludedOptions?.map(mapDataValue);

  const freezedFilter = (): void => {
    setLoading(false);
    setOptions(
      options.filter((o) =>
        debouncedTerm
          .split(" ")
          .filter((s) => !!s)
          .map((s) => s.toLowerCase())
          .map((s) => o.label.toLowerCase().includes(s))
          .reduce((a, b) => a && b, true),
      ),
    );
  };

  const fetch = (): void => {
    setLoading(true);
    orderedRequestHelper
      .ordered(() => optionFetch(debouncedTerm))
      .then((results) => {
        setLoading(false);
        setOptions(results.data.map(mapDataValue));

        if (freeze) {
          setFreeze(results.totalPages === 1);
        }
      })
      .catch(() => {
        setLoading(false);
        setOptions([]);
        setFreeze(false);
      });
  };

  const filterOptions = excludedValues?.length
    ? (selectables: AutocompleteSelectable<T>[]): AutocompleteSelectable<T>[] =>
        selectables.filter((selectable) => !excludedValues.some((val) => val.key === selectable.key))
    : (x: AutocompleteSelectable<T>[]): AutocompleteSelectable<T>[] => x;

  const renderDefaultInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      className={error ? classes.error : ""}
      error={error}
      fullWidth
      helperText={helperText}
      label={mandatory ? `${inputLabel}*` : inputLabel}
      inputProps={{
        ...params.inputProps,
        disabled: !!disabledTextEntry || !!disabled,
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          if (freeSolo) {
            const newItem = (event.target as HTMLInputElement).value.trim();
            const option = options.find((o) => o.label === newItem);
            if (newItem) {
              onChange(event, option ? [...(value || []), option.data!] : [...(value || []), stringToData!(newItem)]);
              setTerm("");
            }
          }
        }
      }}
      placeholder={placeholder}
      variant="outlined"
      margin="normal"
      {...TextFieldProps}
    />
  );

  useImperativeHandle(ref, () => ({
    reset: (): void => {
      setTerm("");
      setFreeze(false);
      fetch();
    },
  }));

  useEffect(() => {
    if (freeze) {
      freezedFilter();
    } else {
      fetch();
    }
  }, [debouncedTerm]);

  useEffect(() => {
    if (!freeze) {
      setLoading(true);
    }
  }, [term]);

  return (
    <Autocomplete
      className={classes.root}
      classes={{ option: classes.option, listbox: classes.listbox }}
      multiple
      disableCloseOnSelect={!!optionsCheckbox}
      disabled={disabled}
      forcePopupIcon={false}
      ListboxProps={{
        style: {
          maxHeight: "10rem",
        },
      }}
      filterOptions={filterOptions}
      noOptionsText={
        freeSolo ? "No matching values found, current value will be created" : "Please enter or adjust the search term"
      }
      onChange={(event, selection): void => {
        if (!keepTermOnChange || isReset(event as React.ChangeEvent<HTMLElement>)) {
          setTerm("");
        }
        onChange(
          event,
          selection.map((selectedItem) => {
            if (freeSolo && typeof selectedItem === "string") {
              return stringToData!(selectedItem);
            }
            return selectedItem.data!;
          }),
        );
      }}
      onInputChange={(event, termValue): void => {
        if (event && event.type === "change") {
          if (term.startsWith(termValue)) {
            setFreeze(false);
          }
          setTerm(termValue);
        }
      }}
      inputValue={term}
      loading={loading}
      value={value ? value.map(mapDataValue) : []}
      loadingText="Loading..."
      options={loading ? [] : options}
      getOptionLabel={(option): string => option?.label || ""}
      getOptionSelected={(option, v): boolean => option.key === v.key}
      renderInput={renderInput || renderDefaultInput}
      renderOption={(option, { inputValue, selected }): ReactElement => {
        const matches = match(option.label, inputValue);
        const parts = parse(option.label, matches);
        return (
          <div className={classes.optionLabel}>
            {optionsCheckbox && (
              <Checkbox
                className={classes.optionCheckbox}
                checked={selected}
                checkedIcon={checkBoxCheckedIcon}
                icon={checkBoxIcon}
                size="small"
              />
            )}
            {parts.map((part: { text: string; highlight: boolean }) => (
              <span key={uuid()} style={{ fontWeight: part.highlight ? 700 : 400, lineHeight: 1 }}>
                {part.text}
              </span>
            ))}
          </div>
        );
      }}
      renderTags={(selection, getTagProps) =>
        selection.map((selectedItem, index) =>
          customTag ? (
            React.createElement(customTag, {
              label: selectedItem.label,
              data: selectedItem.data,
              onClick: () => onTagClick?.({ data: selectedItem.data, index }),
              ...getTagProps({ index }),
            })
          ) : (
            <Chip
              clickable={!!onTagClick}
              onClick={() => onTagClick?.({ data: selectedItem.data, index })}
              color={tagColorSecondary?.(index) ? "secondary" : "primary"}
              deleteIcon={disabled ? <></> : <Clear color="primary" />}
              label={selectedItem.label}
              {...getTagProps({ index })}
              {...(getCustomTagDelete?.(selectedItem, index) || {})}
            />
          ),
        )
      }
    />
  );
}

export default forwardRef(CustomSearchAutocompleteMultiple);
